import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  InputNumber,
  Modal,
  Row,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { ExclamationCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useCan } from '@refinedev/core';
import { Link } from 'react-router-dom';
import { adminApi } from '@/services/api';
import { apiErrorMessage } from '@/utils/apiError';
import { istDateTime } from '@/utils/cricketCalendar';
import {
  PVP_ENV_NAME,
  PVP_FIELDS,
  PVP_FIELD_LABEL,
  PVP_RANGE,
  pvpChangeLines,
  pvpClearBody,
  pvpConfigChanges,
  pvpFieldError,
  pvpFieldSource,
  pvpFormFieldErrors,
  pvpFormValues,
  pvpMatchmakingError,
  pvpPauseBody,
  pvpPauseConfirm,
  pvpValueText,
} from '@/utils/pvpSettings';
import type { PvpField, PvpSettingsForm, PvpSource } from '@/utils/pvpSettings';
import type { PvpConfig, UpdatePvpConfigBody } from '@/types/pvp';

const { Title, Text } = Typography;
const { confirm } = Modal;

const PICKUP_NOTE =
  'The game servers and the PvP server read these within about 30 s. A match already found keeps the fee it was found with.';

const FIELD_HELP: Record<PvpField, string> = {
  entryFee: 'Coins each player stakes on a ranked match. 0 makes every match free (rating only).',
  dailyPaidMatches:
    'Paid matches per player per 24 h; after that their matches are free. 0 = no cap (every match costs the fee).',
  minBalls: 'Balls faced since the last clear before the rule judges a batter.',
  runsPerBall: 'Flag at or above this many runs a ball.',
  sixRate: 'Flag at or above this many sixes a ball.',
  botAfterSec: 'Seconds in the queue before a player may ask for a bot match.',
  botRatingOffset:
    "Added to the player's team rating to pick the bot's squad: above 0 a stronger bot, below 0 a weaker one.",
  startWidth: 'At first, opponents within ± this rating.',
  widerAfterSec: 'Seconds in the queue before the range widens.',
  widerWidth: 'The wider range; not narrower than the starting range.',
  anyoneAfterSec: 'Seconds in the queue before anyone is a match; later than Widen after.',
};

const STEP: Record<PvpField, number> = {
  entryFee: 1,
  dailyPaidMatches: 1,
  minBalls: 1,
  runsPerBall: 0.1,
  sixRate: 0.05,
  botAfterSec: 1,
  botRatingOffset: 10,
  startWidth: 25,
  widerAfterSec: 1,
  widerWidth: 25,
  anyoneAfterSec: 1,
};

const fallbackName = (field: PvpField): string =>
  PVP_ENV_NAME[field] ? `env ${PVP_ENV_NAME[field]}` : 'the default';

/** Form rule: the backend's range check, so a save never meets a 422 for it. */
const rangeRule = (field: PvpField) => ({
  validator: (_: unknown, value: number | null) => {
    const error = pvpFieldError(field, value);
    return error ? Promise.reject(new Error(error)) : Promise.resolve();
  },
});

/** One line of the "In effect now" table. */
interface SettingRow {
  field: PvpField;
  value: number;
  source: PvpSource;
  fallback: number;
}

/**
 * Realtime PvP (Rivals): the pause switch, the entry fee, the daily paid-match
 * cap, the cheat review rule, the bot wait and strength and the matchmaking
 * range, stored on the config row as `pvpConfig`. A field not stored comes from
 * env (fee, cap) or its default; PUT is partial and null clears a field.
 */
export const PvpTab: React.FC = () => {
  const { data: canEdit } = useCan({ resource: 'config', action: 'edit' });
  const editable = canEdit?.can !== false;

  const [form] = Form.useForm<PvpSettingsForm>();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<PvpConfig | null>(null);

  // Puts the values in force in the form and drops any field errors.
  const fill = useCallback(
    (next: PvpConfig) => {
      const values = pvpFormValues(next.effective);
      form.setFields(PVP_FIELDS.map((name) => ({ name, value: values[name], errors: [] })));
    },
    [form]
  );

  const show = useCallback(
    (next: PvpConfig) => {
      setConfig(next);
      fill(next);
    },
    [fill]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await adminApi.pvp.getSettings();
      show(res.data);
    } catch (error) {
      setLoadError(apiErrorMessage(error, 'Failed to load the PvP settings'));
    } finally {
      setLoading(false);
    }
  }, [show]);

  useEffect(() => {
    load();
  }, [load]);

  // Nothing is saved until the settings in force have loaded, so a save can't
  // blindly overwrite values that were never shown.
  const ready = config !== null;

  const send = async (body: UpdatePvpConfigBody, done: string) => {
    setSaving(true);
    try {
      const res = await adminApi.pvp.updateSettings(body);
      show(res.data);
      message.success(done);
    } catch (error) {
      form.setFields(pvpFormFieldErrors(error));
      message.error(apiErrorMessage(error, 'Failed to save the PvP settings'));
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!config) return;
    let values: PvpSettingsForm;
    try {
      values = await form.validateFields();
    } catch {
      return; // the form shows which fields to fix
    }
    const rule = pvpMatchmakingError(values);
    if (rule) {
      form.setFields([{ name: rule.field, errors: [rule.message] }]);
      return;
    }
    const loaded = pvpFormValues(config.effective);
    // Only what this admin changed: an untouched env value stays on env.
    const body = pvpConfigChanges(loaded, values);
    if (Object.keys(body).length === 0) {
      message.info('Nothing changed');
      return;
    }
    confirm({
      title: 'Save the PvP settings?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <Space direction="vertical" size={4}>
          {pvpChangeLines(loaded, body).map((line) => (
            <Text key={line}>{line}</Text>
          ))}
          {body.entryFee === 0 && (
            <Text type="warning">
              Every PvP match becomes free: no entry fee and no coins won, rating only.
            </Text>
          )}
          <Text type="secondary">{PICKUP_NOTE}</Text>
        </Space>
      ),
      okText: 'Save',
      onOk: () => send(body, 'PvP settings saved'),
    });
  };

  const setPaused = (pause: boolean) => {
    const { title, okText, lines } = pvpPauseConfirm(pause);
    confirm({
      title,
      icon: <ExclamationCircleOutlined />,
      content: (
        <Space direction="vertical" size={4}>
          {lines.map((line) => (
            <Text key={line}>{line}</Text>
          ))}
        </Space>
      ),
      okText,
      okButtonProps: pause ? { danger: true } : undefined,
      onOk: () =>
        send(pvpPauseBody(pause), pause ? 'New PvP matches paused' : 'PvP matches resumed'),
    });
  };

  const clear = (row: SettingRow) => {
    const label = PVP_FIELD_LABEL[row.field];
    const from = fallbackName(row.field);
    confirm({
      title: `Use ${from} for the ${label.toLowerCase()}?`,
      icon: <ExclamationCircleOutlined />,
      content: (
        <Space direction="vertical" size={4}>
          <Text>
            {label}: {pvpValueText(row.field, row.value)} → {pvpValueText(row.field, row.fallback)}
          </Text>
          {PVP_ENV_NAME[row.field] && (
            <Text type="secondary">
              That is this API node's env; each server reads its own, so set the value here to be
              sure they all use the same.
            </Text>
          )}
          <Text type="secondary">{PICKUP_NOTE}</Text>
        </Space>
      ),
      okText: 'Clear',
      onOk: () => send(pvpClearBody(row.field), `${label} now comes from ${from}`),
    });
  };

  if (loadError && !ready) {
    return (
      <Card>
        <Alert
          type="error"
          showIcon
          message="Couldn't load the PvP settings"
          description={`${loadError}. Nothing can be changed until the settings in force have loaded.`}
          action={<Button onClick={load}>Retry</Button>}
        />
      </Card>
    );
  }

  const rows: SettingRow[] = config
    ? PVP_FIELDS.map((field) => ({
        field,
        value: pvpFormValues(config.effective)[field],
        ...pvpFieldSource(config, field),
      }))
    : [];

  const input = (field: PvpField) => {
    const { min, max, aboveMin } = PVP_RANGE[field];
    return (
      <Form.Item
        name={field}
        label={PVP_FIELD_LABEL[field]}
        extra={`${FIELD_HELP[field]} ${aboveMin ? `Above ${min}, up to ${max}.` : `${min} to ${max}.`}`}
        rules={[rangeRule(field)]}
      >
        <InputNumber
          min={min}
          max={max}
          step={STEP[field]}
          precision={PVP_RANGE[field].whole ? 0 : undefined}
          style={{ width: '100%' }}
        />
      </Form.Item>
    );
  };

  return (
    <Card loading={loading && !ready}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={5}>Realtime PvP (Rivals)</Title>
          <Text type="secondary">
            Pausing new matches, the coins each player stakes on a ranked PvP match, how many paid
            matches a player gets a day, the rule that flags a batter for cheat review, when a bot
            is offered and how strong it is, and how far apart in rating two players may be. Flagged
            players are on <Link to="/pvp-review">PvP Review</Link>. These only matter while the
            feature flag <Text code>feature.pvp</Text> is ON (Feature Flags tab).
          </Text>
        </div>

        <Alert type="info" showIcon message={PICKUP_NOTE} />

        {/* A failed refresh keeps the last settings on screen. */}
        {loadError && <Alert type="error" showIcon message={loadError} />}

        <Space align="center" wrap>
          <Switch
            aria-label="Pause new matches"
            checked={config?.effective.paused === true}
            disabled={!editable || !ready || saving}
            onChange={setPaused}
          />
          <Text strong>Pause new matches</Text>
          <Text type="secondary">
            Empties the queue and answers every join and bot request with busy; matches being played
            carry on.
          </Text>
        </Space>

        {config?.effective.paused && (
          <Alert
            type="warning"
            showIcon
            message="New PvP matches are paused: nobody can join the queue or start a bot match."
          />
        )}

        {config?.effective.entryFee === 0 && (
          <Alert
            type="warning"
            showIcon
            message="The entry fee is 0: every PvP match is free (rating only)."
          />
        )}

        <div>
          <Space style={{ marginBottom: 8 }} wrap>
            <Text strong>In effect now</Text>
            {config?.updatedAt && (
              <Tooltip title="When the config row was last saved; it also holds autoplay, force update and other settings.">
                <Text type="secondary">Last saved: {istDateTime(config.updatedAt)}</Text>
              </Tooltip>
            )}
            <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>
              Refresh
            </Button>
          </Space>
          <Table<SettingRow>
            size="small"
            rowKey="field"
            pagination={false}
            dataSource={rows}
            columns={[
              {
                title: 'Setting',
                key: 'setting',
                render: (_, r) => PVP_FIELD_LABEL[r.field],
              },
              {
                title: 'Value',
                key: 'value',
                render: (_, r) => <Text strong>{pvpValueText(r.field, r.value)}</Text>,
              },
              {
                title: 'Comes from',
                key: 'source',
                render: (_, r) =>
                  r.source === 'stored' ? (
                    <Space size={4} wrap>
                      <Tag color="blue">Set here</Tag>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {fallbackName(r.field)}: {pvpValueText(r.field, r.fallback)}
                      </Text>
                    </Space>
                  ) : (
                    <Tag>{r.source === 'env' ? `Env ${PVP_ENV_NAME[r.field]}` : 'Default'}</Tag>
                  ),
              },
              {
                title: '',
                key: 'clear',
                render: (_, r) =>
                  r.source === 'stored' && (
                    <Button
                      type="link"
                      size="small"
                      disabled={!editable || saving}
                      onClick={() => clear(r)}
                    >
                      {PVP_ENV_NAME[r.field] ? 'Use env' : 'Use default'}
                    </Button>
                  ),
              },
            ]}
          />
          <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
            Env values are those of the API node that answered. A fee or cap not set here comes from
            each server's own env.
          </Text>
        </div>

        <Form form={form} layout="vertical" disabled={!editable || !ready}>
          <Text strong>Fee and daily cap</Text>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col xs={24} md={12}>
              {input('entryFee')}
            </Col>
            <Col xs={24} md={12}>
              {input('dailyPaidMatches')}
            </Col>
          </Row>
          <Text strong>Cheat review rule</Text>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              A batter is flagged when either rate is met. A new rule is applied when each player's
              next match ends; it does not clear anyone already flagged.
            </Text>
          </div>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col xs={24} md={8}>
              {input('minBalls')}
            </Col>
            <Col xs={24} md={8}>
              {input('runsPerBall')}
            </Col>
            <Col xs={24} md={8}>
              {input('sixRate')}
            </Col>
          </Row>
          <Text strong>Bot</Text>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col xs={24} md={12}>
              {input('botAfterSec')}
            </Col>
            <Col xs={24} md={12}>
              {input('botRatingOffset')}
            </Col>
          </Row>
          <Text strong>Matchmaking range</Text>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Opponents within the starting range, then the wider range after a wait, then anyone.
              The longer-waiting player's range decides. A new range applies on the next pairing
              pass.
            </Text>
          </div>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col xs={24} md={6}>
              {input('startWidth')}
            </Col>
            <Col xs={24} md={6}>
              {input('widerAfterSec')}
            </Col>
            <Col xs={24} md={6}>
              {input('widerWidth')}
            </Col>
            <Col xs={24} md={6}>
              {input('anyoneAfterSec')}
            </Col>
          </Row>
          <Space>
            <Button type="primary" onClick={save} loading={saving}>
              Save PvP settings
            </Button>
            <Button onClick={() => config && fill(config)}>Undo changes</Button>
          </Space>
        </Form>
      </Space>
    </Card>
  );
};
